import { getFormProps, useForm } from '@conform-to/react'
import { Form, Link, useFetcher, useSubmit } from '@remix-run/react'

import { ExternalLinkIcon } from 'lucide-react'
import { useRef } from 'react'
import { FaChevronDown } from 'react-icons/fa'
import { useOptimisticThemeMode } from '#app/__theme'
import { Badge } from '#app/components/ui/badge'
import { Button } from '#app/components/ui/button'
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuPortal,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
} from '#app/components/ui/dropdown-menu'
import { Icon } from '#app/components/ui/icon'
import { type action } from '#app/root'
import { getUserImgSrc } from '#app/utils/misc'
import { type Theme } from '#app/utils/theme.server'
import { useUser } from '#app/utils/user'

export function Logo() {
	return (
		<Link to="/" className="inline-flex items-center gap-2">
			<span className="text-lg font-semibold">Chorducate</span>
			<Badge size="1">Alpha</Badge>
		</Link>
	)
}

export function ThemeSwitch({
	userPreference,
}: {
	userPreference?: Theme | null
}) {
	const fetcher = useFetcher<typeof action>()

	const [form] = useForm({
		id: 'theme-switch',
		lastResult: fetcher.data?.result,
	})

	const optimisticMode = useOptimisticThemeMode()
	const mode = optimisticMode ?? userPreference ?? 'system'
	const nextMode =
		mode === 'system' ? 'light' : mode === 'light' ? 'dark' : 'system'
	const modeLabel = {
		light: (
			<Icon name="sun">
				<span className="sr-only">Light</span>
			</Icon>
		),
		dark: (
			<Icon name="moon">
				<span className="sr-only">Dark</span>
			</Icon>
		),
		system: (
			<Icon name="laptop">
				<span className="sr-only">System</span>
			</Icon>
		),
	}

	return (
		<fetcher.Form method="POST" {...getFormProps(form)}>
			<input type="hidden" name="theme" value={nextMode} />
			<div className="flex gap-2">
				<button
					type="submit"
					className="flex h-8 w-8 cursor-pointer items-center justify-center"
				>
					{modeLabel[mode]}
				</button>
			</div>
		</fetcher.Form>
	)
}

export function UserDropdown() {
	const user = useUser()
	const submit = useSubmit()
	const formRef = useRef<HTMLFormElement>(null)
	return (
		<DropdownMenu>
			<DropdownMenuTrigger asChild>
				<Button asChild variant="secondary">
					<Link
						to={`/users/${user.username}`}
						// this is for progressive enhancement
						onClick={e => e.preventDefault()}
						className="flex items-center gap-2"
					>
						{/* <img
							className="h-8 w-8 rounded-full object-cover"
							alt={user.name ?? user.username}
							src={getUserImgSrc(user.image?.id)}
						/> */}
						<span className="text-body-sm font-bold">
							{user.name ?? user.username}
						</span>
						<FaChevronDown />
					</Link>
				</Button>
			</DropdownMenuTrigger>
			<DropdownMenuPortal>
				<DropdownMenuContent sideOffset={8} align="start">
					<DropdownMenuItem asChild>
						<Link prefetch="intent" to={`/whiteboards/new`}>
							{/* <Icon className="text-body-md" name="avatar"> */}
							{/* </Icon> */}
							Open app
						</Link>
					</DropdownMenuItem>
					{/* <DropdownMenuItem asChild>
						<Link prefetch="intent" to={`/users/${user.username}/whiteboards`}>
							<Icon className="text-body-md" name="pencil-2">
								Your Whiteboards
							</Icon>
						</Link>
					</DropdownMenuItem> */}
					<DropdownMenuSeparator />
					<DropdownMenuItem
						asChild
						// this prevents the menu from closing before the form submission is completed
						onSelect={event => {
							event.preventDefault()
							submit(formRef.current)
						}}
					>
						<Form action="/logout" method="POST" ref={formRef}>
							<Icon className="text-body-md" name="exit">
								<button type="submit">Logout</button>
							</Icon>
						</Form>
					</DropdownMenuItem>
				</DropdownMenuContent>
			</DropdownMenuPortal>
		</DropdownMenu>
	)
}
